import { readableTranscriptLines, type LineOptions } from '@nightshift/ui-log';

import { calendarDate } from './format.ts';

/**
 * Re-export, so views import a local module rather than an alias.
 *
 * The extraction itself lives in `core/src/ui/log-lines.ts` and is shared with the
 * orchestrator: the live log tail and this viewer must summarise the same bytes the same way,
 * or a finished run reads differently from the way the operator watched it run.
 */
export function extractReadableLines(text: string, options?: LineOptions): string[] {
  return readableTranscriptLines(text, options);
}

/** What the transcript route adds to `missing` when retention removed the file. */
export interface TranscriptExpiry {
  expired?: boolean;
  expiredAt?: number;
  retentionDays?: number;
}

/**
 * What a transcript pane says in place of a transcript that is not on disk.
 *
 * A removed transcript is not a fault and should not read like one: the run's outcome, cost
 * and review are all still there, only the raw text has gone, and the policy that removed it
 * is named so nobody goes looking for a bug.
 */
export function missingTranscriptNote(payload: TranscriptExpiry): string {
  if (!payload.expired || payload.expiredAt === undefined) return 'No transcript on disk for this attempt.';
  const policy = payload.retentionDays ? `the ${payload.retentionDays}-day retention policy` : 'the retention policy';
  return `Transcript removed under ${policy}; it expired on ${calendarDate(payload.expiredAt)}.`;
}
